"use client"

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { socketService } from "../../utils/socketService";
import { FiTruck, FiUser, FiCalendar, FiBox, FiCheckCircle, FiClock, FiPlus, FiRss, FiClipboard, FiArrowRight, FiAlertTriangle, FiInfo, FiSearch, FiStar, FiTrendingUp, FiNavigation, FiEye, FiZap, FiRefreshCw, FiSettings } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { canAcceptMoreMissions } from "../../utils/subscriptionLimits";
// --- SOUS-COMPOSANTS UI ---

const StatCard = ({ label, value, icon, gradient = "from-amber-500 to-amber-600", trend = null, delay = 0 }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.5, delay, ease: "easeOut" }}
    whileHover={{ y: -2, scale: 1.02 }}
    className="bg-gradient-to-br from-slate-800/90 via-slate-800/70 to-slate-900/90 backdrop-blur-xl p-6 rounded-3xl border border-slate-700/50 shadow-2xl hover:shadow-amber-500/10 hover:border-amber-500/30 transition-all duration-300 group"
  >
    <div className="flex items-center gap-4">
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br ${gradient} shadow-lg ring-2 ring-amber-500/20 group-hover:ring-amber-500/40 transition-all duration-300`}>
      {icon}
    </div>
      <div className="flex-1">
        <p className="text-3xl font-bold text-white tracking-tight">{value}</p>
        <p className="text-sm font-semibold text-slate-300 tracking-wide">{label}</p>
        {trend && (
          <div className="flex items-center gap-1 mt-1">
            <FiTrendingUp className="w-3 h-3 text-green-400" />
            <span className="text-xs text-green-400 font-medium">{trend}</span>
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

const BroadcastMissionCard = ({ mission, index = 0 }) => {
  const navigate = useNavigate();
  const { from_address, to_address, move_date, volume_m3, price_cents, description, user, created_at } = mission;
  const clientName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : "Client";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (cents) => {
    if (!cents) return "N/A";
    return `${(cents / 100).toLocaleString()} TND`;
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return "N/A";
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return "À l'instant";
    if (diffInHours < 24) return `Il y a ${diffInHours}h`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `Il y a ${diffInDays}j`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-gradient-to-br from-slate-800/90 via-slate-800/70 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-6 relative overflow-hidden group transition-all duration-300 hover:border-amber-500/50 hover:shadow-2xl hover:shadow-amber-500/10"
    >
      {/* Gradient overlay */}
      <div className="absolute top-0 left-0 h-2 w-full bg-gradient-to-r from-transparent via-amber-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      
      {/* Priority badge */}
      <div className="absolute top-4 right-4">
        <div className="bg-gradient-to-r from-amber-500/20 to-amber-600/20 text-amber-400 px-3 py-1 rounded-full text-xs font-bold border border-amber-500/30">
          <FiZap className="inline w-3 h-3 mr-1" />
          Nouveau
        </div>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <FiNavigation className="text-amber-400 w-4 h-4" />
            <span className="text-sm font-semibold text-amber-400 tracking-wide">MISSION DISPONIBLE</span>
          </div>
          <h3 className="text-xl font-bold text-white leading-tight">
            {from_address}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <FiArrowRight className="text-slate-500 w-4 h-4" />
            <span className="text-slate-300 font-medium">{to_address}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0 ml-6">
          <p className="text-3xl font-bold text-amber-400 tracking-tight">{formatPrice(price_cents)}</p>
          <p className="text-xs text-amber-300/80 font-semibold">Budget client</p>
        </div>
      </div>
      
      {/* Mission details */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-700/50 rounded-2xl p-3 border border-slate-600/50">
          <div className="flex items-center gap-2 mb-1">
            <FiCalendar className="text-amber-400 w-4 h-4"/>
            <span className="text-xs font-semibold text-slate-400 tracking-wide">DATE</span>
          </div>
          <span className="text-sm font-bold text-white">{formatDate(move_date)}</span>
        </div>
        
        <div className="bg-slate-700/50 rounded-2xl p-3 border border-slate-600/50">
          <div className="flex items-center gap-2 mb-1">
            <FiBox className="text-amber-400 w-4 h-4"/>
            <span className="text-xs font-semibold text-slate-400 tracking-wide">VOLUME</span>
          </div>
          <span className="text-sm font-bold text-white">{volume_m3 || "N/A"} m³</span>
        </div>
        
        <div className="bg-slate-700/50 rounded-2xl p-3 border border-slate-600/50">
          <div className="flex items-center gap-2 mb-1">
            <FiUser className="text-amber-400 w-4 h-4"/>
            <span className="text-xs font-semibold text-slate-400 tracking-wide">CLIENT</span>
          </div>
          <span className="text-sm font-bold text-white truncate">{clientName}</span>
        </div>
        
        <div className="bg-slate-700/50 rounded-2xl p-3 border border-slate-600/50">
          <div className="flex items-center gap-2 mb-1">
            <FiClock className="text-amber-400 w-4 h-4"/>
            <span className="text-xs font-semibold text-slate-400 tracking-wide">PUBLIÉ</span>
          </div>
          <span className="text-sm font-bold text-white">{getTimeAgo(created_at)}</span>
        </div>
      </div>

      {/* Description */}
      {description && (
        <div className="mb-6 p-4 bg-slate-700/30 rounded-2xl border border-slate-600/30">
          <p className="text-slate-300 text-sm leading-relaxed line-clamp-2">{description}</p>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-slate-400 text-xs">
          <FiEye className="w-3 h-3" />
          <span>Vue par d'autres déménageurs</span>
        </div>
        
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate(`/demenageur/missions/${mission.id}`)} 
          className="px-6 py-3 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:from-amber-600 hover:via-amber-700 hover:to-amber-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-xl hover:shadow-amber-500/25 flex items-center gap-2 border-2 border-amber-400/20"
        >
          <FiEye className="w-4 h-4" />
          Voir & Proposer
        </motion.button>
      </div>
    </motion.div>
  );
};

const MyMissionItem = ({ mission, index = 0 }) => {
    const { from_address, status, move_date, price_cents } = mission;
    const statusConfig = {
      'confirmed': { 
        label: 'Confirmé', 
        color: 'bg-gradient-to-r from-blue-500 to-blue-600',
        bgColor: 'bg-blue-500/10',
        borderColor: 'border-blue-500/30',
        textColor: 'text-blue-400'
      },
      'completed': { 
        label: 'Terminé', 
        color: 'bg-gradient-to-r from-green-500 to-green-600',
        bgColor: 'bg-green-500/10',
        borderColor: 'border-green-500/30',
        textColor: 'text-green-400'
      },
      'pending': { 
        label: 'En attente', 
        color: 'bg-gradient-to-r from-amber-500 to-amber-600',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/30',
        textColor: 'text-amber-400'
      }
    };
    const currentStatus = statusConfig[status] || { 
      label: status, 
      color: 'bg-gradient-to-r from-gray-500 to-gray-600',
      bgColor: 'bg-gray-500/10',
      borderColor: 'border-gray-500/30',
      textColor: 'text-gray-400'
    };

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("fr-FR", {
        day: '2-digit',
        month: 'short'
      });
    };

    const formatPrice = (cents) => {
      if (!cents) return "N/A";
      return `${(cents / 100).toLocaleString()} TND`;
    };

    return (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: index * 0.05 }}
        >
          <Link 
            to={`/demenageur/missions/${mission.id}`} 
            className="block group"
          >
            <div className="flex items-center justify-between p-4 rounded-2xl hover:bg-gradient-to-r hover:from-slate-700/50 hover:to-slate-800/50 transition-all duration-300 border border-slate-700/50 hover:border-slate-600/70 hover:shadow-lg">
              <div className="flex items-center gap-4 flex-1">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${currentStatus.color} shadow-lg`}></div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-white truncate group-hover:text-amber-300 transition-colors">
                    {from_address}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${currentStatus.bgColor} ${currentStatus.textColor} ${currentStatus.borderColor} border`}>
                      {currentStatus.label}
                    </span>
                    <span className="text-xs text-slate-500">
                      {formatDate(move_date)}
                    </span>
                  </div>
                </div>
              </div>
            <div className="flex items-center gap-3">
                {price_cents && (
                  <div className="text-right">
                    <p className="text-xs font-bold text-amber-400">{formatPrice(price_cents)}</p>
                  </div>
                )}
                <FiArrowRight className="text-slate-500 group-hover:text-amber-400 transition-colors"/>
                </div>
            </div>
        </Link>
        </motion.div>
    );
};

// --- COMPOSANT PRINCIPAL ---
export default function DemMissions() {
  const [broadcast, setBroadcast] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subscription, setSubscription] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [monthlyMissionsCount, setMonthlyMissionsCount] = useState(0);
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [priceFilter, setPriceFilter] = useState("all");
  const [volumeFilter, setVolumeFilter] = useState("all");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();

  // Charger l'utilisateur connecté et vérifier la période d'essai
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
        
        // Vérifier la période d'essai
        const isDemenageur = user.is_demenageur === true || user.role === 'demenageur';
        
        // Si trial_end_date n'est pas défini, on considère une période d'essai de 7 jours
        const trialEndDate = user.trial_end_date 
          ? new Date(user.trial_end_date) 
          : user.createdAt 
            ? new Date(new Date(user.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000)
            : null;
        
        const isTrialDateValid = trialEndDate && trialEndDate > new Date();
        const isSubscriptionTrial = !subscription || subscription.status === 'trial';
        const trialActive = isDemenageur && isTrialDateValid && isSubscriptionTrial;
        
        // Calculer les jours restants
        const daysLeft = trialActive && trialEndDate
          ? Math.ceil((trialEndDate - new Date()) / (1000 * 60 * 60 * 24))
          : 0;
        
        setIsTrialActive(trialActive);
        setTrialDaysLeft(daysLeft);
        
        console.log('Détails de la période d\'essai (Missions):', {
          isDemenageur,
          trialEndDate: trialEndDate?.toISOString(),
          isTrialDateValid,
          isSubscriptionTrial,
          trialActive,
          daysLeft,
          subscriptionStatus: subscription?.status
        });
        
      } catch (error) {
        console.error('Erreur lors du chargement du profil utilisateur:', error);
      }
    }
  }, [subscription]);

  // Charger l'abonnement et le nombre de missions du mois
  useEffect(() => {
    if (!currentUser?.id) return;

    const loadSubscriptionData = async () => {
      try {
        console.log('Chargement des données d\'abonnement pour l\'utilisateur:', currentUser.id);
        const [subscriptionRes, missionsRes] = await Promise.all([
          api.getSubscription(currentUser.id).then(res => {
            console.log('Réponse de l\'abonnement:', res);
            return res;
          }).catch((err) => {
            console.error('Erreur lors de la récupération de l\'abonnement:', err);
            return { data: null };
          }),
          api.get(`/subscriptions/movers/${currentUser.id}/monthly-missions`).then(res => {
            console.log('Réponse du nombre de missions:', res);
            return res;
          }).catch((err) => {
            console.error('Erreur lors de la récupération du nombre de missions:', err);
            return { data: { count: 0 } };
          })
        ]);

        setSubscription(subscriptionRes?.data || null);
        setMonthlyMissionsCount(missionsRes?.data?.count || 0);
      } catch (error) {
        console.error('Erreur lors du chargement des données d\'abonnement:', error);
      }
    };

    loadSubscriptionData();
  }, [currentUser?.id]);

  const loadMissions = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [broadcastRes, mineRes] = await Promise.all([
        api.fetchMoverBroadcastQuotes(),
        api.fetchMyMoverQuotes()
      ]);

      // Pour les missions en broadcast, on utilise le statut du serveur ou 'pending' par défaut
      setBroadcast((broadcastRes?.data || []).map(m => ({
        ...m,
        status: m.status || 'pending',
        type: 'broadcast'
      })));

      // Pour les missions personnelles, on utilise le statut du serveur
      setMine((mineRes?.data || []).map(m => ({
        ...m,
        status: m.status || 'pending',
        type: 'mine'
      })));
    } catch (e) {
      setError(e.message || "Erreur de chargement");
      if (!silent) {
        toast.error("Impossible de charger les missions.");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMissions(false);
  }, [loadMissions]);

  useEffect(() => {
    let isMounted = true;
    let debounceTimer = null;
    const token = localStorage.getItem("token");
    if (!token) return undefined;

    const refreshFromRealtime = () => {
      if (!isMounted) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadMissions(true);
      }, 300);
    };

    const disposers = [
      socketService.on("new_service_request", refreshFromRealtime),
      socketService.on("status_updated", refreshFromRealtime),
      socketService.on("service_request_created", refreshFromRealtime),
      socketService.on("client_price_proposed", refreshFromRealtime),
      socketService.on("price_proposed", refreshFromRealtime),
      socketService.on("price_accepted", refreshFromRealtime),
      socketService.on("negotiation_accepted", refreshFromRealtime),
      socketService.on("quick_service_peer_claimed", refreshFromRealtime),
      socketService.on("notification", (payload) => {
        if (
          payload?.type === "new_service_request" ||
          payload?.type === "status_updated" ||
          payload?.type === "client_price_proposed" ||
          payload?.type === "price_proposed" ||
          payload?.type === "price_accepted" ||
          payload?.type === "negotiation_accepted" ||
          payload?.type === "quick_service_peer_claimed"
        ) {
          refreshFromRealtime();
        }
      }),
    ];

    return () => {
      isMounted = false;
      if (debounceTimer) clearTimeout(debounceTimer);
      disposers.forEach((dispose) => {
        if (typeof dispose === "function") dispose();
      });
    };
  }, [loadMissions]);

  // Fonction de filtrage et tri des missions
  const filteredAndSortedBroadcast = useMemo(() => {
    let filtered = broadcast.filter(mission => {
      // Filtre par terme de recherche
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          mission.from_address?.toLowerCase().includes(searchLower) ||
          mission.to_address?.toLowerCase().includes(searchLower) ||
          mission.user?.first_name?.toLowerCase().includes(searchLower) ||
          mission.user?.last_name?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Filtre par prix
      if (priceFilter !== "all") {
        const price = mission.price_cents / 100;
        switch (priceFilter) {
          case "low":
            if (price >= 500) return false;
            break;
          case "medium":
            if (price < 500 || price >= 1000) return false;
            break;
          case "high":
            if (price < 1000) return false;
            break;
          default:
            break;
        }
      }

      // Filtre par volume
      if (volumeFilter !== "all") {
        const volume = mission.volume_m3;
        switch (volumeFilter) {
          case "small":
            if (volume >= 20) return false;
            break;
          case "medium":
            if (volume < 20 || volume >= 50) return false;
            break;
          case "large":
            if (volume < 50) return false;
            break;
          default:
            break;
        }
      }

      return true;
    });

    // Tri
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case "oldest":
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case "price-high":
          return (b.price_cents || 0) - (a.price_cents || 0);
        case "price-low":
          return (a.price_cents || 0) - (b.price_cents || 0);
        case "volume-high":
          return (b.volume_m3 || 0) - (a.volume_m3 || 0);
        case "volume-low":
          return (a.volume_m3 || 0) - (b.volume_m3 || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [broadcast, searchTerm, sortBy, priceFilter, volumeFilter]);

  const stats = useMemo(() => ({
      broadcast: broadcast.length,
      filtered: filteredAndSortedBroadcast.length,
      mine: mine.length,
      confirmed: mine.filter(m => m.status === 'confirmed').length,
      completed: mine.filter(m => m.status === 'completed').length,
      pending: mine.filter(m => m.status === 'pending').length,
  }), [broadcast, filteredAndSortedBroadcast, mine]);

  // Fonction de rafraîchissement
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadMissions(true);
      toast.success("Missions mises à jour !");
    } catch (e) {
      toast.error("Erreur lors du rafraîchissement");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Vérifier les limites d'abonnement et la période d'essai
  const subscriptionStatus = useMemo(() => {
    if (!subscription && !currentUser?.is_demenageur) return null;
    return canAcceptMoreMissions(subscription, monthlyMissionsCount, currentUser);
  }, [subscription, monthlyMissionsCount, currentUser]);

  // Récupérer les détails du plan actuel
  console.log('Données d\'abonnement:', subscription);
  
  const planName = subscription?.plan_name || 'Aucun abonnement';
  const planStatus = subscription?.status === 'active' ? 'Actif' : 'Inactif';
  
  // Vérifier si c'est un abonnement premium
  const isPremium = subscription?.subscription_type === 'premium';
  
  // Récupérer la limite de missions depuis les fonctionnalités du plan
  const monthlyMissionsLimit = isPremium 
    ? 'illimité' 
    : (subscription?.plan_features?.monthly_missions || 0);
    
  const planLimit = isPremium 
    ? 'Illimité' 
    : `${monthlyMissionsCount}/${monthlyMissionsLimit}`;

  if (loading) return <div className="p-8 text-center text-slate-400">Chargement des missions...</div>;
  if (error) return <div className="p-8 text-center text-red-400">{error}</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white p-4 md:p-8 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-amber-600/10"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_50%)]"></div>
      </div>
      
      <div className="max-w-7xl mx-auto space-y-8 relative z-10">
        {/* Bannière de période d'essai */}
        {isTrialActive && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-blue-600 via-indigo-700 to-purple-700 text-white p-6 rounded-3xl shadow-2xl border border-blue-500/20 backdrop-blur-xl"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
                    <FiStar className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-bold text-xl">Période d'essai premium active</h3>
                </div>
                <p className="text-sm opacity-90">
                  Profitez de toutes les fonctionnalités premium pendant encore <span className="font-bold text-yellow-300">{trialDaysLeft} jour{trialDaysLeft > 1 ? 's' : ''}</span>.
                </p>
              </div>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/abonnement')}
                className="mt-3 md:mt-0 px-6 py-3 bg-white text-blue-700 rounded-2xl font-bold hover:bg-blue-50 transition-all duration-300 shadow-xl flex items-center gap-2"
              >
                <FiTrendingUp className="w-4 h-4" />
                S'abonner maintenant
              </motion.button>
            </div>
          </motion.div>
        )}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <FiTruck className="w-6 h-6 text-white" />
                </div>
            <div>
                  <h1 className="text-4xl font-bold text-white tracking-tight">Centre de Missions</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-2 h-2 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full"></div>
                    <p className="text-amber-400 font-semibold">Tableau de bord déménageur</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-300 mt-3">
                {subscription ? (
                  <>
                    <span>Abonnement: <span className="text-amber-400 font-semibold">{planName}</span> • <span className="text-green-400">{planStatus}</span></span>
                    {!isPremium && (
                      <span> • <span className="text-blue-400">{planLimit}</span> missions ce mois</span>
                    )}
                  </>
                ) : (
                  'Chargement de votre abonnement...'
                )}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              {subscription && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 border border-slate-700/50 rounded-2xl px-4 py-3 text-sm backdrop-blur-xl"
                >
                  <div className="text-amber-400 font-bold">{planName}</div>
                  <div className="text-slate-300 text-xs">
                    {isPremium ? 'Missions illimitées' : `Missions: ${planLimit} ce mois`}
                  </div>
                </motion.div>
              )}
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/demenageur/abonnement')} 
                className="px-6 py-3 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white font-bold rounded-2xl flex items-center gap-2 hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 border-2 border-blue-500/20"
              >
                <FiSettings className="w-4 h-4" />
                {subscription ? 'Gérer l\'abonnement' : 'S\'abonner'}
              </motion.button>
             
            </div>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard 
              label="Abonnement" 
              value={planName} 
              icon={<FiCheckCircle className="w-6 h-6 text-white"/>} 
              gradient="from-green-500 to-green-600"
              delay={0.1}
            />
            <StatCard 
              label="Missions ce mois" 
              value={isPremium ? 'Illimitées' : `${monthlyMissionsCount}/${monthlyMissionsLimit}`}
              icon={<FiClipboard className="w-6 h-6 text-white"/>} 
              gradient="from-blue-500 to-blue-600"
              delay={0.2}
            />
            <StatCard 
              label="Missions Confirmées" 
              value={stats.confirmed} 
              icon={<FiCheckCircle className="w-6 h-6 text-white"/>} 
              gradient="from-emerald-500 to-emerald-600"
              delay={0.3}
            />
            <StatCard 
              label="Missions en Attente" 
              value={stats.pending} 
              icon={<FiClock className="w-6 h-6 text-white"/>} 
              gradient="from-amber-500 to-amber-600"
              delay={0.4}
            />
          </div>
        </motion.header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          
          {/* --- Colonne Principale: Broadcast --- */}
          <section className="lg:col-span-2 space-y-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50 shadow-2xl"
            >
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                      <FiRss className="w-4 h-4 text-white" />
                    </div>
                    Missions Disponibles
                  </h2>
                  <p className="text-slate-400 mt-1">{stats.filtered} mission{stats.filtered !== 1 ? 's' : ''} trouvée{stats.filtered !== 1 ? 's' : ''}</p>
                </div>
                
                <div className="flex items-center gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="px-4 py-2 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-white rounded-xl transition-all duration-300 border border-slate-600/50 flex items-center gap-2"
                  >
                    <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Actualiser
                  </motion.button>
                </div>
              </div>

              {/* Barre de recherche et filtres */}
              <div className="space-y-4">
                <div className="relative">
                  <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Rechercher par adresse, client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Trier par</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300"
                    >
                      <option value="newest">Plus récent</option>
                      <option value="oldest">Plus ancien</option>
                      <option value="price-high">Prix élevé</option>
                      <option value="price-low">Prix bas</option>
                      <option value="volume-high">Volume élevé</option>
                      <option value="volume-low">Volume bas</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Prix</label>
                    <select
                      value={priceFilter}
                      onChange={(e) => setPriceFilter(e.target.value)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300"
                    >
                      <option value="all">Tous les prix</option>
                      <option value="low">Moins de 500 TND</option>
                      <option value="medium">500 - 1000 TND</option>
                      <option value="high">Plus de 1000 TND</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Volume</label>
                    <select
                      value={volumeFilter}
                      onChange={(e) => setVolumeFilter(e.target.value)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300"
                    >
                      <option value="all">Tous les volumes</option>
                      <option value="small">Moins de 20 m³</option>
                      <option value="medium">20 - 50 m³</option>
                      <option value="large">Plus de 50 m³</option>
                    </select>
                  </div>
                </div>
              </div>
            </motion.div>
              <div className="space-y-6">
                {!subscription && !isTrialActive ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-amber-500/30 rounded-3xl p-8 text-center shadow-2xl"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-3xl flex items-center justify-center mx-auto mb-4">
                    <FiAlertTriangle className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Abonnement requis</h3>
                  <p className="text-slate-300 mb-6">Pour accéder aux missions, veuillez souscrire à un abonnement.</p>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                      onClick={() => navigate('/demenageur/abonnement')}
                    className="px-8 py-3 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:from-amber-600 hover:via-amber-700 hover:to-amber-800 text-slate-900 font-bold rounded-2xl transition-all duration-300 shadow-xl hover:shadow-amber-500/25"
                    >
                      Voir les offres
                  </motion.button>
                </motion.div>
              ) : !subscriptionStatus?.canAccept ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-amber-500/30 rounded-3xl p-8 text-center shadow-2xl"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-3xl flex items-center justify-center mx-auto mb-4">
                    <FiAlertTriangle className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Limite de missions atteinte</h3>
                  <p className="text-slate-300 mb-6">{subscriptionStatus?.reason}</p>
                    {subscriptionStatus?.upgradeRequired && (
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/demenageur/abonnement')}
                      className="px-8 py-3 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:from-amber-600 hover:via-amber-700 hover:to-amber-800 text-slate-900 font-bold rounded-2xl transition-all duration-300 shadow-xl hover:shadow-amber-500/25"
                      >
                        Améliorer mon abonnement
                    </motion.button>
                    )}
                </motion.div>
              ) : filteredAndSortedBroadcast.length > 0 ? (
                  <>
                    {subscriptionStatus && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/30 rounded-2xl p-4 text-sm text-slate-300 backdrop-blur-sm"
                    >
                      <div className="flex items-center gap-3">
                        <FiInfo className="w-5 h-5 text-blue-400 flex-shrink-0" />
                          <span>{subscriptionStatus.reason}</span>
                      </div>
                    </motion.div>
                    )}
                  <AnimatePresence>
                    {filteredAndSortedBroadcast.map((mission, index) => (
                      <BroadcastMissionCard key={mission.id} mission={mission} index={index} />
                    ))}
                  </AnimatePresence>
                  </>
                ) : (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center p-16 border-2 border-dashed border-slate-700/50 rounded-3xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 backdrop-blur-sm"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-slate-700/50 to-slate-800/50 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-slate-600/50">
                    <FiRss className="h-10 w-10 text-slate-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Aucune mission trouvée</h3>
                  <p className="text-slate-400 mb-4">
                    {searchTerm || priceFilter !== "all" || volumeFilter !== "all" 
                      ? "Aucune mission ne correspond à vos critères de recherche."
                      : "Aucune mission publique pour le moment."
                    }
                  </p>
                  {(searchTerm || priceFilter !== "all" || volumeFilter !== "all") && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setSearchTerm("");
                        setPriceFilter("all");
                        setVolumeFilter("all");
                      }}
                      className="px-6 py-2 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-white rounded-xl transition-all duration-300 border border-slate-600/50"
                    >
                      Effacer les filtres
                    </motion.button>
                  )}
                </motion.div>
                )}
              </div>
          </section>

          {/* --- Colonne Latérale: Mes Devis --- */}
          <aside className="lg:col-span-1 space-y-6">
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-gradient-to-r from-slate-700/80 via-amber-500/20 to-slate-700/80 bg-gradient-to-r from-slate-800/50 to-slate-900/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-purple-500/20">
                    <FiClipboard className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white tracking-wide">Mes Missions</h2>
                    <p className="text-sm text-purple-300/80 mt-1 font-medium">{mine.length} mission{mine.length !== 1 ? 's' : ''} active{mine.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                    {mine.length > 0 ? (
                  mine.map((mission, index) => <MyMissionItem key={mission.id} mission={mission} index={index} />)
                ) : (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center p-8"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-slate-700/50 to-slate-800/50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-600/50">
                      <FiClipboard className="h-8 w-8 text-slate-500" />
                    </div>
                    <p className="text-sm text-slate-400 font-medium">Aucune mission personnelle</p>
                    <p className="text-xs text-slate-500 mt-1">Vos missions apparaîtront ici</p>
                  </motion.div>
                    )}
                </div>
              
              <div className="p-4 border-t border-slate-700/50 bg-gradient-to-r from-slate-800/30 to-slate-900/30">
                <motion.button 
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/demenageur/missions')} 
                  className="w-full text-center text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-4 py-3 rounded-xl border border-amber-500/30 hover:border-amber-500/50"
                >
                        Voir toutes mes missions
                </motion.button>
                </div>
            </motion.div>
          </aside>

        </div>
      </div>
    </div>
  );
}